// ETJump training HUDs: overbounce detector letters, jump speeds, snaphud,
// upmove meter, strafe quality and max speed. The numbers come from
// shared/etjump; this only draws them.

import type { OverbounceResult } from '../../shared/etjump/overbounce';
import { angleNormalizePI, inZone, snapAccel, snapAngles, snapZones, wishOf } from '../../shared/etjump/snaphud';
import type { JumpSpeed } from '../../shared/etjump/trainers';
import { el, setText } from './dom';

export interface EtjHudState {
  ob: OverbounceResult | null;
  jumps: JumpSpeed[] | null;
  /** Snaphud input: view yaw (degrees) and the movement keys. */
  snap: { yaw: number; move: { forwardmove: number; rightmove: number }; sprint: boolean; msec: number; hfov: number } | null;
  upmove: { pre: number; post: number; full: number } | null;
  strafeQuality: number | null;
  maxSpeed: { speed: number; at: number } | null;
}

/** Jumps shown in the list (etj_jumpSpeedsMaxJumps). */
const SHOWN_JUMPS = 10;
const MAX_SPEED_FADE_MS = 2000;
/** etj_upmoveMeterMaxDelay */
const UPMOVE_MAX_DELAY = 360;

export class EtjHud {
  readonly root = el('div', 'hud-etj');
  readonly snapCanvas = el('canvas', 'hud-snap');
  private readonly snapCtx = this.snapCanvas.getContext('2d')!;
  private readonly obEl = el('div', 'hud-ob');
  private readonly jumpsEl = el('div', 'hud-jumps');
  private readonly upmoveEl = el('div', 'hud-upmove');
  private readonly upmoveUp = el('div', 'hud-upmove-up');
  private readonly upmoveDown = el('div', 'hud-upmove-down');
  private readonly upmoveText = el('div', 'hud-upmove-text');
  private readonly sqEl = el('div', 'hud-sq');
  private readonly maxEl = el('div', 'hud-maxspeed');
  private jumpsKey = '';

  constructor() {
    const graph = el('div', 'hud-upmove-graph');
    graph.append(this.upmoveUp, this.upmoveDown);
    this.upmoveEl.append(graph, this.upmoveText);
    this.root.append(this.obEl, this.jumpsEl, this.upmoveEl, this.sqEl, this.maxEl);
  }

  resize(width: number, height: number): void {
    this.snapCanvas.width = width;
    this.snapCanvas.height = 10;
    this.snapCanvas.style.top = `${Math.round(height * 0.5) + 14}px`;
  }

  update(s: EtjHudState, now: number): void {
    // overbounce detector: J(ump) F(all) B(elow) S(ticky), like etj_drawOB
    const ob = s.ob;
    setText(this.obEl, ob ? `${ob.sticky ? 'S' : ''}${ob.fall ? 'F' : ''}${ob.jump ? 'J' : ''}${ob.below ? 'B' : ''}` : '');

    // jump speeds: the last jumps, green when faster than the one before, red when slower
    const jumps = s.jumps;
    this.jumpsEl.style.display = jumps ? '' : 'none';
    if (jumps) {
      const shown = jumps.slice(-SHOWN_JUMPS);
      const key = shown.map((j) => `${j.speed}${j.relation[0]}${j.upmove ?? ''}`).join(',');
      if (key !== this.jumpsKey) {
        this.jumpsKey = key;
        this.jumpsEl.replaceChildren(el('div', 'hud-jumps-label', 'Jump Speeds'), ...shown.map((j) => el('div', `hud-jump ${j.relation}`, j.upmove === null ? String(j.speed) : `${j.speed} (${j.upmove})`)));
      }
    }

    // upmove meter: bar down = jump pressed before landing (or released early), up = held after landing
    const um = s.upmove;
    this.upmoveEl.style.display = um ? '' : 'none';
    if (um) {
      const pre = Math.max(-UPMOVE_MAX_DELAY, Math.min(UPMOVE_MAX_DELAY, um.pre));
      const post = Math.min(UPMOVE_MAX_DELAY, um.post);
      this.upmoveUp.style.height = `${(post / UPMOVE_MAX_DELAY) * 50}%`;
      this.upmoveDown.style.height = `${(Math.abs(pre) / UPMOVE_MAX_DELAY) * 50}%`;
      this.upmoveDown.classList.toggle('early', pre < 0);
      setText(this.upmoveText, `${um.post}\n${um.full}\n${um.pre}`);
    }

    this.sqEl.style.display = s.strafeQuality === null ? 'none' : '';
    if (s.strafeQuality !== null) setText(this.sqEl, `Strafe Quality: ${s.strafeQuality.toFixed(3).slice(0, 5)}%`);

    const ms = s.maxSpeed;
    const age = ms ? now - ms.at : Infinity;
    this.maxEl.style.display = age < MAX_SPEED_FADE_MS ? '' : 'none';
    if (ms && age < MAX_SPEED_FADE_MS) {
      setText(this.maxEl, String(Math.round(ms.speed)));
      this.maxEl.style.opacity = String(Math.min(1, ((MAX_SPEED_FADE_MS - age) / MAX_SPEED_FADE_MS) * 2));
    }

    this.drawSnap(s.snap);
  }

  /** SnaphudV2: alternating zones across the screen, the zone the wish direction is in highlighted. */
  private drawSnap(snap: EtjHudState['snap']): void {
    const ctx = this.snapCtx;
    const w = this.snapCanvas.width;
    const h = this.snapCanvas.height;
    ctx.clearRect(0, 0, w, h);
    if (!snap) return;
    const wish = wishOf(snap.yaw, snap.move, snap.sprint);
    const angles = snapAngles(snapAccel(wish.wishspeed, snap.msec));
    if (angles.length < 2) return;
    const half = (snap.hfov / 2) * (Math.PI / 180);
    // screen x of a world yaw, relative to the wish direction at the centre
    const toX = (a: number) => w / 2 - (angleNormalizePI(a - wish.angle) / half) * (w / 2);
    for (const z of snapZones(angles)) {
      const active = inZone(z.start, z.end, wish.angle);
      const x0 = toX(z.start);
      const x1 = toX(z.end);
      if (Math.abs(x1 - x0) > w / 2) continue; // wraps around the screen edge
      ctx.fillStyle = active ? (z.alt ? 'rgba(255, 210, 90, 0.75)' : 'rgba(110, 220, 255, 0.75)') : z.alt ? 'rgba(255, 255, 255, 0.28)' : 'rgba(30, 30, 30, 0.45)';
      ctx.fillRect(Math.min(x0, x1), 0, Math.abs(x1 - x0), h);
    }
  }
}
